/**
 * User status enum for approval workflow
 */
export enum UserStatus {
  CREATED = 'created',
  PENDING = 'pending',
  APPROVED = 'approved',
  DISABLED = 'disabled',
}

/**
 * Shift status enum
 */
export enum ShiftStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  CLOSED_WITHOUT_ASSIGNMENT = 'closed without assignment',
  FILLED = 'filled',
}

/**
 * Shift reaction status enum
 */
export enum ShiftReactionStatus {
  NOT_REACTED = 'not_reacted',
  APPLIED = 'applied',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  REFUSED = 'refused',
}

/**
 * Days of the week for availability and subscriptions.
 * Limited to Monday-Friday as the system only handles weekday shifts.
 */
export enum WeekDay {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
}

/**
 * Notification types for different events
 */
export enum NotificationType {
  SHIFT_MATCH = 'shift_match',
  SHIFT_APPLICATION = 'shift_application',
  SHIFT_ACCEPTED = 'shift_accepted',
  SHIFT_REJECTED = 'shift_rejected',
  SHIFT_EXPIRED = 'shift_expired',
  NEW_SCHOOL = 'new_school',
  MATCH_COMPLETED = 'match_completed',
}

/**
 * User types for shift application management
 */
export enum UserType {
  TEACHER = 'teacher',
  SCHOOL = 'school',
}

/**
 * Actions that can be performed on teacher applications
 */
export enum TeacherAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}

/**
 * Actions that teachers can take on shifts
 */
export enum TeacherReactionAction {
  APPLY = 'apply',
  REFUSE = 'refuse',
}

/**
 * Valid reaction types for school responses
 */
export const VALID_REACTIONS = ['accepted', 'rejected'] as const;
export type ReactionType = (typeof VALID_REACTIONS)[number];

/**
 * Geographic coordinates
 */
export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Address information
 */
export interface Address {
  address?: string;
  house_number?: string;
  street?: string;
  postal_code?: string;
  city?: string;
}

/**
 * Contact information interface
 */
export interface ContactInfo {
  contact_email?: string | null;
  contact_phone?: string | null;
}

/**
 * User preferences for receiving system notifications
 * through different communication channels.
 */
export interface NotificationSettings {
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled?: boolean;
}

/**
 * Weekly availability interface
 */
export interface WeeklyAvailability {
  general_availability_monday?: boolean;
  general_availability_tuesday?: boolean;
  general_availability_wednesday?: boolean;
  general_availability_thursday?: boolean;
  general_availability_friday?: boolean;
}

/**
 * Error response from Supabase
 */
export interface SupabaseError {
  message: string;
  code?: string;
}

/**
 * Validation state for forms
 */
export interface ValidationState {
  isValid: boolean;
  email: string;
  phone: string;
}

/**
 * Primary education data from external API
 */
export interface PrimaryEducation {
  id: number;
  _id?: number;
  // Basic info
  provincie?: string;
  bevoegd_gezag_nummer?: number;
  instellingscode: string;
  vestigingscode: string;
  vestigingsnaam?: string;
  naam?: string;
  denominatie?: string;
  telefoonnummer?: string;
  internetadres?: string;

  // Address info
  straatnaam?: string;
  huisnummer_toevoeging?: string;
  postcode?: string;
  plaatsnaam?: string;
  plaats?: string;
  adres?: string;

  // Administrative areas
  gemeentenummer?: string;
  gemeentenaam?: string;

  // Correspondence address
  straatnaam_correspondentieadres?: string;
  huisnummer_toevoeging_correspondentieadres?: string;
  postcode_correspondentieadres?: string;
  plaatsnaam_correspondentieadres?: string;

  // Regional codes
  nodaal_gebied_code?: string;
  nodaal_gebied_naam?: string;
  rpa_gebied_code?: string;
  rpa_gebied_naam?: string;
  wgr_gebied_code?: string;
  wgr_gebied_naam?: string;
  coropgebied_code?: string;
  coropgebied_naam?: string;
  onderwijsgebied_code?: string;
  onderwijsgebied_naam?: string;
  rmc_regio_code?: string;
  rmc_regio_naam?: string;
  vakantieregio?: string;
}

/**
 * School profile interface
 */
export interface School extends ContactInfo, NotificationSettings {
  id: string;
  photo_path?: string | null;
  status: UserStatus;
  school_vestigingscode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number | null;
  primary_education?: PrimaryEducation;
  listing?: Listing[];
  auth_created_at?: string | null;
  last_sign_in_at?: string | null;
  connected_teachers_count?: number;
}

/**
 * Teacher profile interface
 */
export interface TeacherProfile
  extends Address,
    Coordinates,
    WeeklyAvailability,
    NotificationSettings {
  id: string;
  status: UserStatus;
  email: string;
  first_name?: string;
  second_name?: string;
  avatar_url?: string;
  phone_number?: string;
  rating?: number;
  associated_schools?: School[];
  auth_created_at?: string | null;
  last_sign_in_at?: string | null;
}

/**
 * School listing interface
 */
export interface Listing {
  id: number;
  created_at: string;
  school_id: string;
  intro: string | null;
  role: string | null;
  rules: string | null;
  supplies: string | null;
  listing_completed: boolean;
  user_school: School;
  shifts: Shift[];
  subscriptions_count?: number;
}

/**
 * Teacher subscription to school listings
 */
export interface Subscription {
  id: string;
  listing_id: string;
  teacher_id: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
}

/**
 * Connected user data for admin portal
 * Represents a teacher connected to a school via subscription
 */
export interface ConnectedUser {
  id: string;
  first_name: string | null;
  second_name: string | null;
  email?: string;
  phone_number?: string | null;
  photo_path?: string | null;
  subscription_days?: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
  };
}

/**
 * Basic shift information
 */
export interface BaseShift {
  id: string;
  shift_date: string;
  time_from: string;
  time_till: string;
  group: string;
  status: ShiftStatus;
}

/**
 * Full shift interface (for school management)
 */
export interface Shift extends BaseShift, ContactInfo {
  school_id: string;
  teacher_id?: string;
  listing_id?: string;
  school_enabled_whatsapp: boolean;
  shift_reactions?: ShiftReaction[];
  user_teacher?: TeacherProfile;
  listing?: {
    id: string;
    intro?: string;
    role?: string;
    rules?: string;
    supplies?: string;
  } | null;
}

/**
 * Teacher-view shift interface (simplified with school name)
 */
export interface TeacherShift extends BaseShift {
  school_id?: string;
  listing_id?: string;
  school_name?: string;
  school_photo_path?: string;
  reaction_status?: ShiftReactionStatus;
  school_enabled_whatsapp?: boolean;
  contact_email?: string;
  contact_phone?: string;
  school_address?: string;
}

/**
 * Data for creating a new shift
 */
export interface CreateShiftData extends ContactInfo {
  group: string;
  shift_date: string;
  time_from: string;
  time_till: string;
  listing_id?: string;
  school_enabled_whatsapp?: boolean;
}

/**
 * Shift reaction (teacher's response to a shift)
 */
export interface ShiftReaction {
  id: string;
  shift_id: string;
  teacher_id: string;
  reaction_time: string;
  status: ShiftReactionStatus;
  second_substitute_name?: string;
  user_teacher: ApplicationTeacher;
}

/**
 * Teacher application with full details
 */
export interface TeacherApplication {
  id: string;
  shift_id: string;
  teacher_id: string;
  status: ShiftReactionStatus;
  reaction_time: string;
  second_substitute_name?: string;
  teacher: {
    id: string;
    first_name: string | null;
    second_name: string | null;
    avatar_url: string | null;
    phone_number: string | null;
    email: string | null;
    rating: number | null;
  } & WeeklyAvailability;
}

/**
 * Teacher data as it appears in applications (simplified version of TeacherProfile)
 */
export type ApplicationTeacher = TeacherApplication['teacher'];

/**
 * School data for matching operations
 */
export interface SchoolMatchingData {
  id: string;
  status: string;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Response from shift creation
 */
export interface ShiftResponse {
  success: boolean;
  message: string;
  data: {
    shift: Shift;
    matching?: {
      success: boolean;
      matches_created: number;
      notifications_queued: number;
    };
  };
}

/**
 * Request payload for school reaction to teacher application
 */
export interface SchoolReactionRequest {
  reaction: ReactionType;
}

/**
 * Response from school reaction
 */
export interface SchoolReactionResponse {
  success: boolean;
  message: string;
  shift_id: string;
  teacher_id: string;
  reaction: ReactionType;
  notifications_sent: {
    email: boolean;
    sms: boolean;
  };
}

/**
 * Response for getting shift applications
 */
export interface ShiftApplicationsResponse {
  shift_id: string;
  shift_date: string;
  time_from: string;
  time_till: string;
  group: string;
  applications: TeacherApplication[];
  total_applications: number;
}

/**
 * Matching service result data
 */
export interface MatchingResult {
  success: boolean;
  message: string;
  total_matches?: number;
  teachers_processed?: number;
  schools_processed?: number;
  relationships_created?: number;
  radius_km?: number;
}

/**
 * Base notification payload interface
 */
export interface BaseNotificationPayload {
  notification_type: NotificationType;
  recipient_id: string;
  email_address?: string;
  phone_number?: string;
}

/**
 * Notification payload for shift-related events
 */
export interface ShiftNotificationPayload extends BaseNotificationPayload {
  notification_type:
    | NotificationType.SHIFT_MATCH
    | NotificationType.SHIFT_ACCEPTED
    | NotificationType.SHIFT_REJECTED;
  shift_id: string;
  shift_date: string;
  shift_time_from: string;
  shift_time_till: string;
  school_name?: string;
  teacher_id?: string;
  listing_details?: {
    role?: string;
    intro?: string;
  };
}

/**
 * Notification payload for school application events
 */
export interface SchoolApplicationNotificationPayload extends BaseNotificationPayload {
  notification_type: NotificationType.SHIFT_APPLICATION;
  shift_id: string;
  teacher_id: string;
  teacher_name: string;
  shift_date: string;
  shift_time_from: string;
  shift_time_till: string;
}

/**
 * Notification payload for new school in region
 */
export interface NewSchoolNotificationPayload extends BaseNotificationPayload {
  notification_type: NotificationType.NEW_SCHOOL;
  school_id: string;
  listing_id?: string;
}

/**
 * Payload for notifying schools when a shift opportunity expires without being filled.
 *
 * This payload is used by the shift expiration cron job to send notifications to
 * the school that posted the shift (to inform them no teacher was assigned).
 *
 * The notification system respects the school's communication preferences (email/SMS)
 * and only sends notifications through channels they have enabled.
 *
 * Teacher application statuses are updated in the database but no notifications are sent to them.
 */
export interface ShiftExpirationNotificationPayload {
  shift_id: string;
  school: {
    contact_info: {
      email?: string;
      phone?: string;
    };
    preferences: {
      sms_enabled: boolean;
      email_enabled: boolean;
    };
  };
  teachers: Array<{
    contact_info: {
      email?: string;
      phone?: string;
    };
    preferences: {
      sms_enabled: boolean;
      email_enabled: boolean;
    };
  }>;
}

export interface TeacherReactionNotificationPayload {
  recipient_contact: string;
  teacher_id: string;
  shift_date: string;
  shift_time_from: string;
  shift_time_till: string;
  group: string;
  reaction: ShiftReactionStatus.APPLIED | ShiftReactionStatus.REFUSED;
}

export interface CustomNotificationPayload {
  recipient_contact: string;
  subject: string;
  message: string;
}

export interface NotificationResult {
  success: boolean;
  /** Server-side default/fallback message (English). Prefer translating via `code`. */
  message: string;
  messageId?: string;
  error?: string;
  /**
   * Stable, locale-independent identifier for the outcome (e.g. 'smsSendFailed').
   * Maps to a `toast.<code>` / `toast.<code>Description` key in the locale files
   * so the UI can show a translated message instead of the raw `message`.
   */
  code?: string;
}

/**
 * Review interface for teacher/school feedback
 * Matches the database table structure where either teacher OR school fields can be null
 */
export interface Review {
  id: number;
  created_at: string;
  shift_id: string | null;
  school_id: string;
  teacher_id: string | null;
  teacher_rating: number | null;
  teacher_comment: string | null;
  teacher_timestamp: string | null;
  school_rating: number | null;
  school_comment: string | null;
  school_timestamp: string | null;
}

/**
 * Data for creating a teacher review
 */
export interface CreateTeacherReviewData {
  shift_id: string;
  rating: number;
  comment?: string;
}

/**
 * Data for creating a school review
 */
export interface CreateSchoolReviewData {
  shift_id: string;
  teacher_id: string;
  rating: number;
  comment?: string;
}

// ReviewsService Response Interfaces
export interface ReviewResponse {
  success: boolean;
  message: string;
  data: Review;
  teacherRating?: number | null; // Updated teacher rating after school review
  schoolRating?: number | null; // Updated school rating after teacher review
}

export interface AllReviewsResponse {
  success: boolean;
  data: Review[];
  total: number;
  globalStatistics: {
    teacher: {
      averageRating: number | null;
      totalRatings: number;
    };
    school: {
      averageRating: number | null;
      totalRatings: number;
    };
  };
}

export interface TeacherReviewsResponse {
  success: boolean;
  data: Review[];
  total: number;
  teacherStatistics: {
    teacherId: string;
    averageRating: number | null;
    totalRatings: number;
    teacherName: string | null;
  };
}

export interface SchoolReviewsResponse {
  success: boolean;
  data: Review[];
  total: number;
  schoolStatistics: {
    schoolId: string;
    averageRating: number | null;
    totalRatings: number;
    schoolName?: string;
  };
}
